import * as React from "react";
import type { MediaEntry } from "../../../ui/components/MediaUploader";
import * as mediaStyles from "../../../ui/components/MediaUploader/MediaUploader.style.css";
import { isYoutubeUrl } from "../../lib/media/youtube";

const EXT_LABELS: Record<string, string> = {
  jpg: "JPEG",
  jpeg: "JPEG",
  png: "PNG",
  gif: "GIF",
  webp: "WebP",
  svg: "SVG",
  mp4: "MP4",
  webm: "WebM",
  mov: "MOV",
  m4v: "M4V",
};

function extensionFromPath(path: string | null | undefined): string | null {
  if (!path) return null;
  const clean = path.split("?")[0]?.split("#")[0] ?? "";
  const base = clean.includes("/") ? clean.slice(clean.lastIndexOf("/") + 1) : clean;
  const dot = base.lastIndexOf(".");
  if (dot < 0 || dot === base.length - 1) return null;
  return base.slice(dot + 1).toLowerCase();
}

function labelFromMime(mime: string): string | null {
  const normalized = mime.toLowerCase();
  if (normalized === "image/jpeg") return "JPEG";
  if (normalized === "image/png") return "PNG";
  if (normalized === "image/gif") return "GIF";
  if (normalized === "image/webp") return "WebP";
  if (normalized === "image/svg+xml") return "SVG";
  if (normalized.startsWith("image/")) return "Image";
  if (normalized === "video/mp4") return "MP4";
  if (normalized === "video/webm") return "WebM";
  if (normalized === "video/quicktime") return "MOV";
  if (normalized.startsWith("video/")) return "Video";
  return null;
}

function labelFromExt(ext: string): string | null {
  return EXT_LABELS[ext] ?? null;
}

/** True only for watch/embed-style YouTube URLs — not posters or data: blobs. */
function isYoutubeWatchUrl(url: string | null | undefined): boolean {
  if (!url || url.startsWith("data:")) return false;
  // Poster images live on img.youtube.com and must label as Image/JPEG, not YouTube.
  if (/img\.youtube\.com\//i.test(url)) return false;
  return isYoutubeUrl(url);
}

function labelFromUrlOrId(url: string, id?: string, previewUrl?: string): string | null {
  const dataMime = url.match(/^data:([^;,]+)/i)?.[1];
  if (dataMime) return labelFromMime(dataMime);

  const ext =
    extensionFromPath(url) ??
    extensionFromPath(id) ??
    extensionFromPath(previewUrl);
  if (ext) return labelFromExt(ext);

  return null;
}

/** Human-readable type label for data-transfer media preview tiles. */
export function mediaEntryTypeLabel(entry: MediaEntry): string {
  // Role from our preview builders wins over URL sniffing (data: base64 can
  // falsely match the loose YouTube regex via a `v/` substring).
  if (
    entry.id.startsWith("hero-") ||
    entry.id.startsWith("thumb-") ||
    entry.id.startsWith("flag-")
  ) {
    return (
      labelFromUrlOrId(entry.url, entry.id, entry.previewUrl) ?? "Image"
    );
  }

  if (isYoutubeWatchUrl(entry.url)) return "YouTube";

  const fromUrl = labelFromUrlOrId(entry.url, entry.id, entry.previewUrl);
  if (fromUrl) return fromUrl;

  if (entry.id.startsWith("video-")) return "Video";

  return "File";
}

export function MediaTypeBadge({ entry }: { entry: MediaEntry }) {
  return (
    <span className={mediaStyles.thumbMainBadge}>{mediaEntryTypeLabel(entry)}</span>
  );
}

export function renderMediaTypeBadge(entry: MediaEntry): React.ReactNode {
  return <MediaTypeBadge entry={entry} />;
}
